import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Box, Stack, useTheme, alpha, Button } from '@mui/material'
import { useLocalStorageState } from 'ahooks'
import { Message } from '@cx/ui'
import { useNavigate } from 'react-router-dom'
import { useRequest } from 'ahooks'
import { AuthContext } from '@/layout/Auth'
import { ApplicationInfo } from '@/types'
import { confirm } from '@/components/Modal'
import { encodeUrl, isSafari } from '@/utils'
import { useMobileView } from '@/hooks'

import { getApplicationsByOrgId, getUserOrganizations, setDefaultOrg, getUserInfo, subscribeApplication } from '@/api'
import CreateNewSpaceModal from './CreateNewSpaceModal'

const Apps = () => {
  const { data: userInfo } = useRequest(getUserInfo)
  const navigate = useNavigate()
  const isMobile = useMobileView()
  const [recentlyUseMap, setRecentlyUseMap] = useLocalStorageState<{ [key: string]: ApplicationInfo[] }>(
    'RECENTLY_USE_APP_MAP',
    {
      defaultValue: {},
    },
  )
  const [createNewSpaceModalVisible, setCreateNewSpaceModalVisible] = useState(false)
  const [appList, setAppList] = useState<ApplicationInfo[]>([])
  const { authInfo, updateAuthInfo } = useContext(AuthContext)
  const { data: orgData, refresh: refreshGetOrganizations } = useRequest(getUserOrganizations)
  const [selectOrg, setSelectOrg] = useState(authInfo?.orgInfo)

  const theme = useTheme()

  const onChangeOrg = (orgInfo: any) => {
    return setDefaultOrg({ ID: orgInfo.id }).then(() => {
      updateAuthInfo({ orgInfo })
      Message.success('切换空间成功')
    })
  }

  const lruCache = (app: ApplicationInfo) => {
    const currentlyUseList = recentlyUseMap?.[selectOrg?.id!] || []
    const index = currentlyUseList?.findIndex((item) => item.id === app.id)

    if (index !== -1) {
      return
    }

    const newList = [app, ...currentlyUseList!].slice(0, 4)
    setRecentlyUseMap({
      ...recentlyUseMap,
      [selectOrg?.id!]: newList,
    })
    return newList
  }

  const enterApp = (app: ApplicationInfo) => {
    if (
      app.certification_date &&
      app.certification_duration === 0 &&
      userInfo?.is_certified !== 1 &&
      app.require_real_name_cert === 1
    ) {
      confirm({
        title: '提示',
        content: '为响应《网络安全法》并保障您的权益，该应用需要您进行实名认证方可使用。',
        okText: '去认证',
        cancelText: '暂不认证',
        closeAfterClose: true,
        onConfirm: () => navigate('/console/personal'),
        onClose: () => {},
      })
    } else {
      requestToSubscribe(app).then(() => {
        lruCache(app)
        const matchKey = app.url_prefix.split('.')[0].match(/^https?:\/\/(.+)/)
        window.open(
          `/console/app/${matchKey ? matchKey[1] : ''}${encodeUrl(app.url_prefix)}`,
          isMobile || isSafari() ? '_self' : '_blank',
        )
      })
    }
  }

  const requestToSubscribe = (app: ApplicationInfo) => {
    return subscribeApplication({ id: app.id, org_id: selectOrg?.id!, package_id: 12 })
  }

  const recentlyUseList = useMemo(() => {
    return recentlyUseMap?.[authInfo?.orgInfo?.id!] || []
  }, [recentlyUseMap, authInfo?.orgInfo?.id])

  useEffect(() => {
    if (authInfo?.orgInfo?.id) {
      setSelectOrg(authInfo?.orgInfo)
      if (authInfo?.orgInfo) {
        getApplicationsByOrgId(authInfo?.orgInfo.id).then((res) => {
          // res.apps.unshift({
          //   name: '最近使用',
          //   apps: recentlyUseMap?.[authInfo.orgInfo!.id!] || [],
          // })
          setAppList(res.apps)
        })
      }
    }
  }, [authInfo?.orgInfo?.id])

  return (
    <Stack gap={5}>
      <CreateNewSpaceModal
        open={createNewSpaceModalVisible}
        onOk={(org: any) => {
          onChangeOrg(org)
          refreshGetOrganizations()
        }}
        onCancel={() => {
          setCreateNewSpaceModalVisible(false)
        }}
      />

      <Stack alignItems='flex-start' id='workbench-app-list' sx={{ width: 1200, mx: 'auto' }}>
        {recentlyUseList.length > 0 && (
          <>
            <Box component='h3' sx={{ fontSize: 16, color: '#000', fontWeight: 600 }}>
              最近使用
            </Box>
            <Stack direction='row' flexWrap='wrap' gap={3} sx={{ mt: 1.5, mb: '44px' }}>
              {recentlyUseList.map((app, index) => {
                return (
                  <Stack
                    gap={1.5}
                    key={app.id}
                    sx={{
                      p: 2.5,
                      width: 282,
                      height: 96,
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      borderRadius: 2,
                      '&:hover': {
                        zIndex: 1,
                        boxShadow: '0px 10px 20px 0px rgba(0,28,85,0.08)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      enterApp(app)
                    }}
                  >
                    <Stack gap={1} direction='row' alignItems='flex-start'>
                      <Box component='img' src={app.logo_url} sx={{ width: 24, height: 24, borderRadius: 2 }} />
                      <Box sx={{ color: '#000', fontWeight: 'bold' }} className='workbench-app-name'>
                        {app.name}
                      </Box>
                    </Stack>
                    <Stack direction='row' gap={1} flexWrap='wrap' sx={{ height: 20, overflow: 'hidden' }}>
                      {app.labels?.length &&
                        app.labels.map((label) => (
                          <Box
                            key={label}
                            sx={{
                              border: '1px solid',
                              borderColor:
                                label === '热门推荐'
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : alpha(theme.palette.primary.main, 0.1),
                              borderRadius: 1,
                              backgroundColor:
                                label === '热门推荐'
                                  ? alpha(theme.palette.error.main, 0.06)
                                  : alpha(theme.palette.primary.main, 0.06),
                              px: 0.5,
                              color:
                                label === '热门推荐'
                                  ? alpha(theme.palette.error.main, 0.7)
                                  : alpha(theme.palette.primary.main, 0.7),
                              fontSize: 12,
                            }}
                          >
                            {label}
                          </Box>
                        ))}
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          </>
        )}

        <Box component='h3' sx={{ fontSize: 16, mb: 1.5, color: '#000', fontWeight: 600 }}>
          应用库
        </Box>
        <Stack direction='row' flexWrap='wrap' gap={3}>
          {appList.map((app, index) => {
            return (
              <Stack
                gap={1.5}
                key={app.id}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  width: 384,
                  height: 142,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  borderRadius: 2,
                  '&:hover': {
                    zIndex: 1,
                    boxShadow: '0px 10px 20px 0px rgba(0,28,85,0.08)',
                    transform: 'translateY(-4px)',
                    '.workbench-app-footer': {
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                <Stack gap={1} direction='row' alignItems='flex-start'>
                  <Box component='img' src={app.logo_url} sx={{ width: 24, height: 24, borderRadius: 2 }} />
                  <Box sx={{ color: '#000', fontWeight: 'bold' }} className='workbench-app-name'>
                    {app.name}
                  </Box>
                </Stack>
                <Stack direction='row' gap={1} flexWrap='wrap' sx={{ height: 20, overflow: 'hidden' }}>
                  {app.labels?.length &&
                    app.labels.map((label) => (
                      <Box
                        key={label}
                        className='workbench-app-category'
                        sx={{
                          border: '1px solid',
                          borderColor:
                            label === '热门推荐'
                              ? alpha(theme.palette.error.main, 0.1)
                              : alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 1,
                          backgroundColor:
                            label === '热门推荐'
                              ? alpha(theme.palette.error.main, 0.06)
                              : alpha(theme.palette.primary.main, 0.06),
                          px: 0.5,
                          color:
                            label === '热门推荐'
                              ? alpha(theme.palette.error.main, 0.7)
                              : alpha(theme.palette.primary.main, 0.7),
                          fontSize: 12,
                        }}
                      >
                        {label}
                      </Box>
                    ))}
                </Stack>
                <Box
                  sx={{
                    fontSize: 12,
                    color: 'rgba(0,0,0,.4)',
                    maxHeight: 36,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {app.description}
                </Box>
                <Stack
                  className='workbench-app-footer'
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-end'
                  gap={2}
                  sx={{
                    transition: 'all 0.1s',
                    pt: '26px',
                    pr: 2.5,
                    position: 'absolute',
                    bottom: 0,
                    height: 100,
                    right: 0,
                    left: 0,
                    background: 'linear-gradient( 180deg, rgba(255,255,255,0.7) 0%, #FFFFFF 100%)',
                    transform: 'translateY(100%)',
                  }}
                >
                  <Button variant='outlined' size='small' href={app.landing_url} target='_blank'>
                    应用介绍
                  </Button>
                  <Button
                    variant='contained'
                    size='small'
                    onClick={() => {
                      enterApp(app)
                    }}
                  >
                    打开应用
                  </Button>
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Apps
